import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { Pagination } from '@/shared/components/custom/pagination';
import type { PaginationProps } from '@/shared/components/custom/pagination';
import type { TimeEntryDto } from '@/features/time-tracking/public';
import { getTimeEntryColumns } from '../adapters/timeEntryColumns';

interface TimeEntriesTableProps {
  entries: TimeEntryDto[];
  isLoading: boolean;
  isError: boolean;
  pagination?: PaginationProps;
  onTaskClick?: (taskId: string) => void;
}

export function TimeEntriesTable({
  entries,
  isLoading,
  isError,
  pagination,
  onTaskClick,
}: TimeEntriesTableProps) {
  const { t } = useTranslation('reports');

  let content;

  if (isLoading) {
    content = (
      <div className="flex flex-col gap-2" data-testid="reports-table-loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  } else if (isError) {
    content = (
      <p
        className="py-6 text-center text-sm text-muted-foreground"
        data-testid="reports-table-error"
      >
        {t('table.error')}
      </p>
    );
  } else {
    content = (
      <ListView
        data={entries}
        columns={getTimeEntryColumns(t, onTaskClick)}
        getRowId={(entry) => entry.id ?? ''}
        data-testid="reports-table"
      />
    );
  }

  return (
    <>
      {content}
      {pagination && <Pagination {...pagination} />}
    </>
  );
}
